import type { ClarifyFormBlockData, ClarifyFormField } from '../../../types';
import type { FieldValue } from './fields';

// A machine value must never be able to close or split the <ctx:…> marker.
const sanitize = (raw: string): string => raw.replace(/[>;\n]/g, ' ').trim();

// Builds the message the form submits. The pending template has two zones with
// DIFFERENT substitution rules: the visible part gets human labels; anything
// inside the agent's own <ctx:…> marker gets MACHINE values (labels there
// leaked fragments into the bubble — an option label containing '>' terminated
// the marker early and left a stray '.>' on screen — and broke the agent's ids).
export const buildSubmitMessage = (
  data: ClarifyFormBlockData,
  values: Record<string, FieldValue>,
  substitution: (_field: ClarifyFormField, _value: FieldValue) => string
): string => {
  const machineValue = (key: string): string => {
    const v = values[key] ?? '';
    return sanitize(String(Array.isArray(v) ? v.join(',') : v));
  };
  const substituteAll = (tpl: string, fn: (_key: string) => string) =>
    data.actions.reduce(
      (acc, field) =>
        acc.replace(new RegExp(`\\{${field.key}\\}`, 'g'), fn(field.key)),
      tpl
    );

  const markerStart = data.warning.indexOf('<ctx:');
  const visibleTpl =
    markerStart >= 0 ? data.warning.slice(0, markerStart) : data.warning;
  const readable = substituteAll(visibleTpl, (key) => {
    const field = data.actions.find((f) => f.key === key);
    return field ? substitution(field, values[key] ?? '') : '';
  }).trim();

  // ONE merged marker: the agent's inline marker (it carries constants the
  // form never showed — salary numbers, interview rounds, icp hints) plus
  // any form field it didn't already mention. Two markers rendered two
  // "+N fields" chips for what is one submission.
  let ctxContent = '';
  if (markerStart >= 0) {
    const rawMarker = data.warning.slice(markerStart);
    const inner = rawMarker.replace(/^<ctx:/, '').replace(/>\s*$/, '');
    ctxContent = substituteAll(inner, machineValue);
  }
  if (!ctxContent.startsWith('fields=') && !/^\w+=/.test(ctxContent)) {
    ctxContent = `fields=${ctxContent}`;
  }
  for (const f of data.actions) {
    if (!new RegExp(`(^|[=;\\s])${f.key}=`).test(ctxContent)) {
      ctxContent += `${ctxContent ? '; ' : 'fields='}${f.key}=${machineValue(f.key)}`;
    }
  }
  return `${readable} <ctx:${ctxContent}>`;
};
